import { strFromU8, strToU8, unzipSync, zipSync } from 'fflate';
import { cleanGeometry, type ShapeGeometry, type ShapeKind } from './geojson';

/** "lon,lat[,alt] lon,lat …" as [lon, lat] pairs. */
function coordinatesOf(text: string): number[][] {
  return text
    .trim()
    .split(/\s+/)
    .map((tuple) => tuple.split(',').slice(0, 2).map(Number))
    .filter((p) => p.length === 2 && p.every(Number.isFinite));
}

/** Contents of every `<tag>…</tag>` (any namespace prefix), in document order. */
function blocks(xml: string, tag: string): string[] {
  const re = new RegExp(`<(?:[\\w-]+:)?${tag}\\b[^>]*>([\\s\\S]*?)</(?:[\\w-]+:)?${tag}>`, 'g');
  return [...xml.matchAll(re)].map((m) => m[1]!);
}
const coordinatesIn = (xml: string) => blocks(xml, 'coordinates').map(coordinatesOf);

/**
 * The shape of the wanted kind in a KML document (Google Earth, My Maps, QGIS…): the longest
 * route, or the area with the largest outline (with its holes). Null when there is none.
 */
export function parseKml(xml: string, kind: ShapeKind): ShapeGeometry | null {
  if (kind === 'LineString') {
    const routes = blocks(xml, 'LineString').flatMap(coordinatesIn);
    const longest = routes.sort((a, b) => b.length - a.length)[0];
    return longest ? cleanGeometry({ type: 'LineString', coordinates: longest } as never) : null;
  }
  const areas = blocks(xml, 'Polygon').map((p) => {
    const outer = blocks(p, 'outerBoundaryIs').flatMap(coordinatesIn)[0] ?? [];
    const inner = blocks(p, 'innerBoundaryIs').flatMap(coordinatesIn);
    return [outer, ...inner];
  });
  const largest = areas.sort((a, b) => b[0]!.length - a[0]!.length)[0];
  return largest ? cleanGeometry({ type: 'Polygon', coordinates: largest } as never) : null;
}

/** The KML document inside a KMZ (a zip): doc.kml, or else the first .kml file. */
export function kmzToKml(data: Uint8Array): string | null {
  const files = unzipSync(data, { filter: (f) => f.name.toLowerCase().endsWith('.kml') });
  const names = Object.keys(files);
  const name = names.find((n) => n.toLowerCase().endsWith('doc.kml')) ?? names[0];
  return name ? strFromU8(files[name]!) : null;
}

const escapeXml = (s: string) =>
  s.replace(
    /[<>&'"]/g,
    (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', "'": '&apos;', '"': '&quot;' })[c]!,
  );
const kmlCoords = (points: number[][]) => points.map(([lon, lat]) => `${lon},${lat},0`).join(' ');
/** KML colours are aabbggrr. */
const kmlColor = (hex: string, alpha: string) => {
  const h = /^#([0-9a-f]{6})$/i.exec(hex)?.[1] ?? '0b3f82';
  return `${alpha}${h.slice(4, 6)}${h.slice(2, 4)}${h.slice(0, 2)}`;
};

/** The shape as a KML document with one Placemark, in the shape's colour. */
export function shapeToKml(g: ShapeGeometry | null | undefined, name: string, color = '#0b3f82'): string {
  const geometry = cleanGeometry(g);
  if (!geometry) return '';
  const body =
    geometry.type === 'LineString'
      ? `<LineString><tessellate>1</tessellate><coordinates>${kmlCoords(geometry.coordinates as number[][])}</coordinates></LineString>`
      : `<Polygon>${(geometry.coordinates as number[][][])
          .map(
            (ring, i) =>
              `<${i ? 'innerBoundaryIs' : 'outerBoundaryIs'}><LinearRing><coordinates>${kmlCoords(ring)}</coordinates></LinearRing></${i ? 'innerBoundaryIs' : 'outerBoundaryIs'}>`,
          )
          .join('')}</Polygon>`;
  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
<Document>
<name>${escapeXml(name)}</name>
<Style id="shape"><LineStyle><color>${kmlColor(color, 'ff')}</color><width>4</width></LineStyle><PolyStyle><color>${kmlColor(color, '55')}</color></PolyStyle></Style>
<Placemark><name>${escapeXml(name)}</name><styleUrl>#shape</styleUrl>${body}</Placemark>
</Document>
</kml>
`;
}

/** The same document zipped as a KMZ. */
export function shapeToKmz(
  g: ShapeGeometry | null | undefined,
  name: string,
  color?: string,
): Uint8Array | null {
  const kml = shapeToKml(g, name, color);
  return kml ? zipSync({ 'doc.kml': strToU8(kml) }) : null;
}
